import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AppShell } from '@/shared/components/app-shell'
import { RouteErrorBoundary } from '@/shared/components/route-error-boundary'
import { RedirectWithToast } from '@/shared/components/redirect-with-toast'
import { ProtectedRoute } from '@/features/auth/components/protected-route'
import { ClubGate } from '@/features/auth/components/club-gate'
import {
  RoleGate,
  PermissionGate,
  type RoleTier,
} from '@/features/staff/components/role-gate'
import type { ClubPermission } from '@/core/models/staff'
import { Skeleton } from '@/shared/ui'

const LoginPage = lazy(() => import('@/features/auth/pages/login-page').then((m) => ({ default: m.LoginPage })))
const SelfServeSignupPage = lazy(() => import('@/features/auth/pages/self-serve-signup-page').then((m) => ({ default: m.SelfServeSignupPage })))
const SignupContinuationPage = lazy(() => import('@/features/auth/pages/signup-continuation-page').then(m => ({ default: m.SignupContinuationPage })))
const ClubPickerPage = lazy(() =>
  import('@/features/auth/pages/club-picker-page').then((m) => ({ default: m.ClubPickerPage })),
)
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth/pages/forgot-password-page').then((m) => ({ default: m.ForgotPasswordPage })),
)
const AuthActionPage = lazy(() =>
  import('@/features/auth/pages/auth-action-page').then((m) => ({ default: m.AuthActionPage })),
)
const EmailVerificationPage = lazy(() =>
  import('@/features/auth/pages/email-verification-page').then((m) => ({ default: m.EmailVerificationPage })),
)
const AcceptInvitePage = lazy(() =>
  import('@/features/auth/pages/accept-invite-page').then((m) => ({ default: m.AcceptInvitePage })),
)
const OnboardingPage = lazy(() =>
  import('@/features/onboarding/pages/onboarding-page').then((m) => ({ default: m.OnboardingPage })),
)
const NoClubAccessPage = lazy(() =>
  import('@/features/auth/pages/no-club-access-page').then((m) => ({ default: m.NoClubAccessPage })),
)
const SuspendedPage = lazy(() =>
  import('@/features/auth/pages/suspended-page').then((m) => ({ default: m.SuspendedPage })),
)
const PendingDeletionPage = lazy(() =>
  import('@/features/auth/pages/pending-deletion-page').then((m) => ({
    default: m.PendingDeletionPage,
  })),
)
const PreviewRedeemPage = lazy(() =>
  import('@/features/preview-session/pages/preview-redeem-page').then((m) => ({
    default: m.PreviewRedeemPage,
  })),
)
const DashboardPage = lazy(() =>
  import('@/features/dashboard/pages/dashboard-page').then((m) => ({ default: m.DashboardPage })),
)
const BookingsPage = lazy(() =>
  import('@/features/bookings/pages/bookings-page').then((m) => ({ default: m.BookingsPage })),
)
const CalendarPage = lazy(() =>
  import('@/features/calendar/pages/calendar-page').then((m) => ({ default: m.CalendarPage })),
)
const SeriesPage = lazy(() => import('@/features/series/pages/series-page').then((m) => ({ default: m.SeriesPage })))
const MembersPage = lazy(() =>
  import('@/features/members/pages/members-page').then((m) => ({ default: m.MembersPage })),
)

const ActivityPage = lazy(() =>
  import('@/features/activity/pages/activity-page').then((m) => ({ default: m.ActivityPage })),
)
const CourtsPage = lazy(() => import('@/features/courts/pages/courts-page').then((m) => ({ default: m.CourtsPage })))
const AnalyticsPage = lazy(() =>
  import('@/features/analytics/pages/analytics-page').then((m) => ({ default: m.AnalyticsPage })),
)
const CoachReportPage = lazy(() =>
  import('@/features/coaches/pages/coach-report-page').then((m) => ({ default: m.CoachReportPage })),
)
const TournamentsPage = lazy(() =>
  import('@/features/tournaments/pages/tournaments-page').then((m) => ({ default: m.TournamentsPage })),
)
const TournamentDetailPage = lazy(() =>
  import('@/features/tournaments/pages/tournament-detail-page').then((m) => ({ default: m.TournamentDetailPage })),
)
const StaffPage = lazy(() => import('@/features/staff/pages/staff-page').then((m) => ({ default: m.StaffPage })))
// Ingresos — owner's view of revenue received from bookings. Lives as its own
// top-level route (was briefly folded into Settings → Facturación; that was
// wrong because Facturación is about the subscription invoices WE issue to
// the club, not their customer payments).
const RevenuePage = lazy(() =>
  import('@/features/payments/pages/payments-page').then((m) => ({ default: m.PaymentsPage })),
)
// Ventas — ancillary sales (balls/grips/gear), counter or match-upsell.
// Staff-tier: reception runs the desk counter sale (the core padel case).
const VentasPage = lazy(() =>
  import('@/features/sales/pages/ventas-page').then((m) => ({ default: m.VentasPage })),
)
const CatalogPage = lazy(() =>
  import('@/features/sales/pages/catalog-page').then((m) => ({ default: m.CatalogPage })),
)
// Producto lives as a Club tab (/settings?tab=producto). Old URL redirects.
const SettingsPage = lazy(() =>
  import('@/features/settings/pages/settings-page').then((m) => ({ default: m.SettingsPage })),
)
const PlaygroundPage = lazy(() =>
  import('@/features/playground/pages/playground-page').then((m) => ({ default: m.PlaygroundPage })),
)
const ProductPage = lazy(() =>
  import('@/features/product/pages/product-page').then((m) => ({ default: m.ProductPage })),
)
const AdminShell = lazy(() =>
  import('@/features/admin/components/admin-shell').then((m) => ({ default: m.AdminShell })),
)
// Dev/E2E-only visual fixture for CobrarSurface (4 payment states). Gated by
// VITE_E2E_HELPERS so it's tree-shaken out of prod — see the fixture page +
// test/e2e/visual/cobrar-surface.spec.ts.
const CobrarFixturesPage = lazy(() =>
  import('@/features/bookings/__fixtures__/cobrar-fixtures-page').then((m) => ({
    default: m.CobrarFixturesPage,
  })),
)
const AdminHealthPage = lazy(() =>
  import('@/features/admin/pages/admin-health-page').then((m) => ({ default: m.AdminHealthPage })),
)
const AdminEmailTemplatesPage = lazy(() =>
  import('@/features/admin/pages/admin-email-templates-page').then((m) => ({
    default: m.AdminEmailTemplatesPage,
  })),
)

// Same 3-block skeleton `<RoleGate>` / `<PermissionGate>` already show while
// THEIR OWN role/admin/preview lookups are in flight (staff-page.tsx and
// friends never see a gap because that skeleton covers it). It did NOT cover
// the moment right after: once a gate clears and hands off to `children` —
// `suspended(<SomePage/>)`, i.e. this Suspense boundary — the lazy route
// chunk still has to resolve, and `fallback={null}` painted nothing for that
// whole window. Usually sub-second and invisible, but on a slow first load
// (cold dev-server transform, a throttled connection, a busy machine) it
// stretched into a multi-second BLANK `<main>` with zero console errors —
// reproduced directly: a fresh sign-in navigating straight to
// `/settings?tab=modules` caught it in ~1/15 hard-reload runs, main empty for
// several seconds before the real content appeared. Reusing the gates' own
// skeleton means a slow load reads as "still loading", never "broken", and a
// fast load reads as one continuous skeleton-to-content transition (no
// separate flash) instead of skeleton → blank → content.
function RouteLoadingSkeleton() {
  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
      <Skeleton height={32} width="40%" />
      <Skeleton height={180} />
      <Skeleton height={180} />
    </div>
  )
}

// Exported (not just used locally) so the Suspense-fallback wiring is
// directly regression-tested — see `index.test.tsx` — without standing up
// the whole `createBrowserRouter` tree.
export function suspended(node: ReactNode): ReactNode {
  // The route-level error boundary wraps each route so a render error in one
  // feature page is contained to its own main-area — the sidebar + topbar
  // stay live, letting the user navigate away without reloading.
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<RouteLoadingSkeleton />}>{node}</Suspense>
    </RouteErrorBoundary>
  )
}

function gated(requiredRole: RoleTier, node: ReactNode): ReactNode {
  return <RoleGate requiredRole={requiredRole}>{suspended(node)}</RoleGate>
}

function gatedByPermission(requiredPermission: ClubPermission, node: ReactNode): ReactNode {
  return (
    <PermissionGate requiredPermission={requiredPermission}>
      {suspended(node)}
    </PermissionGate>
  )
}

export const router = createBrowserRouter([
  { path: '/login', element: suspended(<LoginPage />) },
  { path: '/signup/continue', element: suspended(<SignupContinuationPage />) },
  { path: '/signup/resume', element: suspended(<SignupContinuationPage />) },
  { path: '/forgot-password', element: suspended(<ForgotPasswordPage />) },
  { path: '/auth/action', element: suspended(<AuthActionPage />) },
  { path: '/accept-invite', element: suspended(<AcceptInvitePage />) },
  // Preview-token redemption. Calls `redeem_preview_token` to mint a
  // Firebase custom auth token, signs the visitor in, then redirects them
  // into the real app where Firestore rules + the preview-session UI gate
  // enforce read-only mode. See docs/preview-mockup-and-trial-flow.md.
  { path: '/preview', element: suspended(<PreviewRedeemPage />) },
  {
    path: '/signup',
    element: suspended(<SelfServeSignupPage />),
  },
  // /__ URLs bypass the existing service-worker navigation fallback.
  { path: '/__signup', element: suspended(<SelfServeSignupPage />) },
  {
    path: '/verify-email',
    element: (
      <ProtectedRoute>
        {suspended(<EmailVerificationPage />)}
      </ProtectedRoute>
    ),
  },
  {
    path: '/select-club',
    element: (
      <ProtectedRoute>
        {suspended(<ClubPickerPage />)}
      </ProtectedRoute>
    ),
  },
  {
    path: '/onboarding',
    element: (
      <ProtectedRoute>
        {suspended(<OnboardingPage />)}
      </ProtectedRoute>
    ),
  },
  // Dead-end pages reached by `<ClubGate>`. They sit outside the `ClubGate`
  // wrapper to avoid redirect loops — the gate routes users *to* them, and
  // the only way out is sign-out or (for no-club-access) the signup CTA.
  {
    path: '/no-club-access',
    element: (
      <ProtectedRoute>
        {suspended(<NoClubAccessPage />)}
      </ProtectedRoute>
    ),
  },
  {
    path: '/suspended',
    element: (
      <ProtectedRoute>
        {suspended(<SuspendedPage />)}
      </ProtectedRoute>
    ),
  },
  {
    path: '/pending-deletion',
    element: (
      <ProtectedRoute>
        {suspended(<PendingDeletionPage />)}
      </ProtectedRoute>
    ),
  },
  {
    element: (
      <ProtectedRoute>
        <ClubGate>
          <AppShell />
        </ClubGate>
      </ProtectedRoute>
    ),
    // Per-route role gates: receptionists see only Día a día items
    // (Hoy / Calendario / Reservas / Jugadores / Actividad). Manager-tier
    // routes are gated below. Preview-token sessions bypass tier gating
    // inside `<RoleGate>` so prospects can evaluate every screen in
    // read-only mode. Platform admins always see everything.
    children: [
      { index: true, element: suspended(<DashboardPage />) },
      { path: 'bookings', element: suspended(<BookingsPage />) },
      // View-permission gates: an owner can revoke these per role in
      // Personal → Permisos por rol; the route must honour that (the nav
      // link is gated on the same permission). All three are receptionist
      // defaults, so default behaviour is unchanged — only an explicit
      // removal hides them. Hoy (index) stays ungated on purpose: it's the
      // landing + deny-redirect target, so gating it would soft-lock a user.
      { path: 'calendar', element: gatedByPermission('viewCalendar', <CalendarPage />) },
      // Legacy /schedule alias — redirect to /calendar so bookmarks + old links survive.
      { path: 'schedule', element: <Navigate to="/calendar" replace /> },
      // Series (recurring bookings) is a Día-a-día operational tool;
      // receptionists manage roster + check-ins. screen-role-matrix.md §7
      // lists Agente as a secondary role.
      { path: 'series', element: suspended(<SeriesPage />) },
      { path: 'members', element: gatedByPermission('viewCrm', <MembersPage />) },
      { path: 'activity', element: gatedByPermission('viewActivityLog', <ActivityPage />) },
      { path: 'courts', element: gated('manager', <CourtsPage />) },
      { path: 'staff', element: gated('manager', <StaffPage />) },

      { path: 'settings', element: gated('manager', <SettingsPage />) },
      // Permission-gated (configurable in Ajustes → Preferencias) — replaced
      // tier gates in the 2026-05-27 permissions refactor.
      { path: 'ingresos', element: gatedByPermission('viewIngresos', <RevenuePage />) },
      { path: 'ventas', element: gatedByPermission('createSale', <VentasPage />) },
      { path: 'catalogo', element: gatedByPermission('manageCatalog', <CatalogPage />) },
      // Analytics is permission-gated on `viewAnalytics` (default O/M,
      // grantable to any role via Personal → roles) — completes the
      // 2026-05-27 refactor; the AnalyticsPage body keeps a defense-in-depth
      // `viewAnalytics` check. Previously a tier gate, which silently ignored
      // the permission (a granted receptionist stayed blocked).
      { path: 'analytics', element: gatedByPermission('viewAnalytics', <AnalyticsPage />) },
      // Per-coach report (#101). Same viewAnalytics gate as Rendimiento; the
      // page + nav link additionally require coachRoleLive (the feature gate).
      { path: 'coaches', element: gatedByPermission('viewAnalytics', <CoachReportPage />) },
      { path: 'tournaments', element: gatedByPermission('createBooking', <TournamentsPage />) },
      { path: 'tournaments/:id', element: gatedByPermission('createBooking', <TournamentDetailPage />) },
      // Legacy redirects. `replace` keeps old URLs / bookmarks working without
      // dirtying back-button history.
      { path: 'payments', element: <Navigate to="/ingresos" replace /> },
      { path: 'product', element: suspended(<ProductPage />) },
      // Note: admin + playground routes used to live here but moved to
      // the top-level AdminShell tree below so they bypass ClubGate.
      // `/admin/*` was previously routed here, inside ClubGate. It now
      // lives at the top level — see the AdminShell route below — so
      // admins without a club selected can land on `/admin/clubes`
      // without being redirected to `/select-club` first.
      //
      // `/playground` was admin-only and moved into `/admin/playground`
      // for the same reason. Legacy URL stays as a redirect.
      { path: 'playground', element: <Navigate to="/admin/playground" replace /> },
    ],
  },
  // ── Admin surface ────────────────────────────────────────────────────────
  // Routed at the top level — explicitly OUTSIDE ClubGate — so admins
  // without a `clubId` land on `/admin/clubes` directly instead of being
  // bounced through `/select-club`. The pages are system-wide and don't
  // need a club context. AdminShell handles the admin-only guard +
  // chrome (top bar + centered tab strip).
  {
    path: '/admin',
    element: (
      <ProtectedRoute>
        {suspended(<AdminShell />)}
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/admin/clubes" replace /> },
      { path: 'clubes', element: <ClubPickerPage embedded /> },
      { path: 'salud', element: <AdminHealthPage /> },
      // `/admin/drift` folded into Salud as the "Integridad de datos" panel.
      // Redirect keeps old bookmarks / deep-links alive.
      { path: 'drift', element: <Navigate to="/admin/salud" replace /> },
      { path: 'plantillas', element: <AdminEmailTemplatesPage /> },
      { path: 'playground', element: <PlaygroundPage /> },
    ],
  },
  // Dev/E2E-only: public (no auth) CobrarSurface visual fixture. Present only
  // when VITE_E2E_HELPERS is on (dev + build:dev/preview); statically dropped
  // from the prod bundle.
  ...(import.meta.env.VITE_E2E_HELPERS === 'true'
    ? [{ path: '/__fixtures/cobrar', element: suspended(<CobrarFixturesPage />) }]
    : []),
  // Unknown route — toast on bounce so the user knows the URL didn't match
  // anything, instead of landing silently on the dashboard with no clue why
  // their typed URL / bookmark stopped working.
  { path: '*', element: <RedirectWithToast title="Esta página no existe" variant="info" /> },
])
